import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Send cookies with every cross-origin request so HttpOnly auth cookies work.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken'));

  // In-flight refresh promise — shared across concurrent 401 retries so only
  // one token refresh request is ever sent at a time.
  const refreshPromiseRef = useRef(null);

  const logout = useCallback(async () => {
    // Capture the refresh token BEFORE clearing state so we can still send it
    // to the server even if this is called without await.
    const storedRefresh = localStorage.getItem('refreshToken');

    // Clear local state synchronously (happens before the first await, so
    // callers that do NOT await still get an immediate logout on the client).
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    // Tell the server to revoke the refresh token and clear HttpOnly cookies.
    // Fire-and-forget: a network error must never block the client-side logout.
    try {
      await axios.post(`${API}/auth/logout`, { refresh_token: storedRefresh });
    } catch {
      // Best-effort — local state is already cleared above
    }
  }, []);

  const refreshAccessToken = useCallback(async () => {
    // If there's already a refresh in flight, reuse that promise — do NOT
    // send a second request because the server rotates the refresh token on
    // first use and the second call would fail with an invalid-token error.
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const storedRefresh = localStorage.getItem('refreshToken');
    if (!storedRefresh) {
      logout();
      return null;
    }

    const promise = axios
      .post(`${API}/auth/refresh`, { refresh_token: storedRefresh })
      .then(({ data }) => {
        const { access_token, refresh_token, user: userData } = data;
        localStorage.setItem('accessToken', access_token);
        localStorage.setItem('refreshToken', refresh_token);
        setAccessToken(access_token);
        setRefreshToken(refresh_token);
        setUser(userData);
        return access_token;
      })
      .catch(() => {
        logout();
        return null;
      })
      .finally(() => {
        // Clear the shared ref so the next real expiry triggers a new request.
        refreshPromiseRef.current = null;
      });

    refreshPromiseRef.current = promise;
    return promise;
  }, [logout]);

  // Setup axios interceptor
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token && config.url?.includes('/api/')) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Double-submit CSRF: echo the csrf_token cookie as a request header
        // for mutating requests.  The server rejects requests where the header
        // and the cookie don't match, blocking cross-site form submissions.
        // When Authorization is present the server skips the CSRF check, but
        // we still send the header as belt-and-suspenders.
        const mutating = ['post', 'put', 'patch', 'delete'].includes(
          (config.method || '').toLowerCase()
        );
        if (mutating) {
          const csrfToken = document.cookie
            .split('; ')
            .find((c) => c.startsWith('csrf_token='))
            ?.split('=')[1];
          if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};
        const url = originalRequest.url || '';

        // A 401 from an AUTH operation itself (login / refresh / register / logout)
        // is a normal auth result — NOT an expired access token. It must propagate
        // straight to the caller. Trying to "refresh" here sent a stale /auth/refresh
        // mid-login, which raced with the login response and left the flow hanging /
        // the admin-key modal not opening. Only refresh-and-retry for OTHER endpoints.
        const isAuthEndpoint =
          url.includes('/auth/login') ||
          url.includes('/auth/refresh') ||
          url.includes('/auth/register') ||
          url.includes('/auth/logout');

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          originalRequest._retry = true;

          const newToken = await refreshAccessToken();
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [refreshAccessToken]);

  // Check auth on mount — fire /auth/me and pre-warm /channels in parallel
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        setUser(response.data);
      } catch (error) {
        await refreshAccessToken();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [refreshAccessToken]);

  const login = async (email, password, adminKey) => {
    const body = { email, password };
    if (adminKey) body.admin_key = adminKey;
    const response = await axios.post(`${API}/auth/login`, body);
    const { access_token, refresh_token, user: userData } = response.data;
    
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    setAccessToken(access_token);
    setRefreshToken(refresh_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { access_token, refresh_token, user: userData } = response.data;
    
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    setAccessToken(access_token);
    setRefreshToken(refresh_token);
    setUser(userData);
    
    return userData;
  };

  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  const value = {
    user,
    loading,
    accessToken,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
    isAuthenticated: !!user,
    isSubscribed: user?.is_subscribed || false,
    isPro: user?.is_pro || false,
    isCreator: user?.role === 'creator' || user?.role === 'admin',
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
